import React, {FC} from "react"
import styled, {css} from "styled-components"
import PropTypes from "prop-types"

import {DealBrandSearchResponse} from "@irati/types"
import {H3, H4} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {RouterLink} from "@irati/ui/src/Link"
import FormattedMessage from "@irati/utils/lang"
import perksTracking from "@irati-tracking/perks/perks"
import DealPages from "@irati-web/perks/src/enums/DealPages"
import withPerkOrReward, {WithPerkOrReward} from "@irati-web/flexi-rewards/src/utils/withPerkOrReward"
import Slider from "@irati/ui/src/Slider"
import {categorySliderSettings, sliderSettings} from "../constants/brandsResultsSliderSettings"

interface Props extends WithPerkOrReward {
  dealBrands?: DealBrandSearchResponse[]
  onClick?: () => void
  wasClickedFromCategories?: boolean
  hasCategorySettings?: boolean
  wasAccessedFromRelevantBrands?: boolean
  wasClickedFromDealPage?: boolean
  isZeroState?: boolean
}

const DealBrandResults: FC<Props> = (
  {
    dealBrands = [],
    onClick = () => {},
    wasClickedFromCategories,
    hasCategorySettings,
    wasAccessedFromRelevantBrands,
    wasClickedFromDealPage,
    isRewards,
    isZeroState = false
  },
  context
) => {
  if (!dealBrands.length || isRewards) {
    return null
  }

  const handleClick = (brand: DealBrandSearchResponse) => {
    const getSource = () => {
      switch (true) {
        case wasClickedFromCategories:
          return DealPages.CATEGORY
        case wasAccessedFromRelevantBrands:
          return DealPages.BRAND
        case wasClickedFromDealPage:
          return DealPages.PERK
        default:
          return DealPages.SEARCH
      }
    }
    perksTracking.clickBrand({
      label: brand.label!,
      id: brand?.brand?.uuid!,
      source: getSource(),
      type: isZeroState ? "zero_state" : undefined
    })
    onClick()
  }

  const pickHeader = () => {
    if (isZeroState) {
      return <FormattedMessage id="platform.search.recommendedBrands.header" values={{count: dealBrands.length || 0}} />
    } else {
      if (wasAccessedFromRelevantBrands) {
        return <FormattedMessage id="platform.search.relevantBrands.header" values={{count: dealBrands.length || 0}} />
      }
      return <FormattedMessage id="platform.search.dealBrands.headerV2" values={{count: dealBrands.length || 0}} />
    }
  }

  return (
    <>
      <H3 marginTop={-0.5} marginBottom={1}>
        {pickHeader()}
      </H3>
      <Slider settings={hasCategorySettings ? categorySliderSettings : sliderSettings}>
        {dealBrands.map((brand) => (
          <RouterLink
            plain
            to={`brands/${brand.slug}`}
            app={context.apps.PERKS}
            key={brand.slug}
            margin={[2, 0]}
            onClick={() => handleClick(brand)}>
            <Flex>
              <Flex direction="column" alignItems="center">
                <BrandLogo logoUrl={brand.logoUrl} />
                <H4 marginTop textAlign="center" width="112px" ellipsis>
                  {brand.label}
                </H4>
              </Flex>
            </Flex>
          </RouterLink>
        ))}
      </Slider>
    </>
  )
}

export default withPerkOrReward(DealBrandResults)

DealBrandResults.contextTypes = {
  apps: PropTypes.object.isRequired
}

export const BrandLogo = styled.div<{logoUrl: string}>`
  ${({theme, logoUrl}) => css`
    background-image: url(${logoUrl});
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 100%;
    width: 64px;
    height: 64px;
    border: 1px solid ${theme.colors.greys.mediumLight};
    margin-bottom: ${theme.spacing.one};
  `}
`
